import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Briefcase,
  Calendar,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  MapPin,
  MessageSquare,
  Bookmark,
  ShieldCheck,
  Star,
  Wallet,
  XCircle,
} from 'lucide-react';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { formatCurrency, upsertSavedVendor } from '../../data/ownerPrototype';
import { useOwnerManpro } from '../../context/OwnerManproContext';
import { useQuery } from '@tanstack/react-query';
import { ownerBidService, manproQueryKeys, BidStatus } from '../../../api/manpro';
import type { OwnerProjectBidDto } from '../../../api/manpro';
import { useVendorFavorite } from '../../hooks/useVendorFavorite';
import { useCreateChannel } from '../../../hooks/useChat';
import { resolveStorageImageUrl } from '../../../lib/imageUrl';

const statusTone: Record<BidStatus, string> = {
  pending: 'bg-blue-50 text-blue-700',
  submitted: 'bg-blue-50 text-blue-700',
  accepted: 'bg-emerald-100 text-emerald-800',
  denied: 'bg-red-50 text-red-700',
  rejected: 'bg-red-50 text-red-700',
  withdrawn: 'bg-slate-100 text-slate-500',
};

const statusLabel: Record<BidStatus, string> = {
  pending: 'Perlu Review',
  submitted: 'Perlu Review',
  accepted: 'Diterima',
  denied: 'Ditolak',
  rejected: 'Ditolak',
  withdrawn: 'Ditarik Provider',
};

const bidDocumentCategoryLabels: Record<string, string> = {
  portfolio: 'Portfolio',
  company_profile: 'Company Profile',
  rab: 'RAB / Breakdown Harga',
  method: 'Metode Kerja',
  legal: 'Legalitas / Sertifikat',
  other: 'Lainnya',
};

const formatDate = (value?: string) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const formatFileSize = (size: number) => {
  if (!size) return 'Ukuran tidak tersedia';

  const units = ['B', 'KB', 'MB', 'GB'];
  const unitIndex = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
  const value = size / 1024 ** unitIndex;

  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const getVendorScore = (providerName: string) => {
  const seed = providerName.split('').reduce((total, char) => total + char.charCodeAt(0), 0);

  return {
    rating: Number((4.6 + (seed % 4) / 10).toFixed(1)),
    reviews: 48 + (seed % 120),
    verified: seed % 3 !== 0,
  };
};

const getProviderName = (bid: OwnerProjectBidDto) =>
  bid.provider?.company_name ||
  bid.provider?.name ||
  bid.provider?.contact_person_name ||
  'Provider'; 

const getBidAmount = (bid: OwnerProjectBidDto) => bid.amount ?? bid.offered_price ?? 0;

const getBidNote = (bid: OwnerProjectBidDto) =>
  bid.note || bid.provider_note || 'Provider mengirim penawaran untuk proyek ini.';

const isReviewableBid = (bid: OwnerProjectBidDto) => ['submitted', 'pending'].includes(bid.status);

const PenawaranDetail = () => {
  const { projectId, bidId } = useParams();
  const navigate = useNavigate();

  const createChannelMutation = useCreateChannel();

  const handleDownload = async (document: any) => {
    if (!document.file_path) return;
    
    try {
      const toastId = toast.loading('Mengunduh dokumen...');
      const url = resolveStorageImageUrl(document.file_path);
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Gagal memuat file');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = window.document.createElement('a');
      link.style.display = 'none';
      link.href = blobUrl;
      link.download = document.name || 'document';
      
      window.document.body.appendChild(link);
      link.click();
      
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success('Dokumen berhasil diunduh', { id: toastId });
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Gagal mengunduh dokumen. Pastikan file tersedia.');
    }
  };

  const handleChatVendor = (vendorId: string | undefined, projectId: string | undefined) => {
    if (!vendorId) return;
    
    createChannelMutation.mutate(
      { to_user: vendorId, reference_id: projectId },
      {
        onSuccess: (data) => {
          navigate(`/owner/pesan?channel_id=${data.id}`);
        },
        onError: (error: any) => {
          console.error('Chat vendor error:', error);
          const errMsg = error?.response?.data?.message || error?.message || 'Terjadi kesalahan sistem.';
          toast.error(`Gagal membuka obrolan: ${errMsg}`);
        }
      }
    );
  };

  const { toggleVendorFavorite, isVendorSaved, isAdding } = useVendorFavorite();
  const { acceptBid, rejectBid, isAcceptingBid, isRejectingBid, getProjectById } = useOwnerManpro();

  const bidQuery = useQuery({
    queryKey: manproQueryKeys.ownerProjectBid(projectId as string, bidId as string),
    queryFn: () => ownerBidService.getOwnerProjectBid(projectId as string, bidId as string),
    enabled: Boolean(projectId && bidId),
  });



  const bid = bidQuery.data;
  const project = (bid as any)?.project || getProjectById(projectId as string);
  // const provider = selectedRow?.provider; 


  if (!project || !bid) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
        <FileText size={40} className="mx-auto text-slate-300" />
        <h1 className="mt-4 text-xl font-extrabold text-slate-900">
          {bidQuery.isLoading ? 'Memuat detail penawaran...' : 'Detail penawaran tidak ditemukan'}
        </h1>
        <Link to="/owner/penawaran-masuk" className="mt-5 inline-flex rounded-xl bg-blue-600 px-5 py-3 text-sm font-extrabold text-white">
          Kembali ke Penawaran Masuk
        </Link>
      </div>
    );
  }


  const providerName = getProviderName(bid);
  const providerId = bid?.provider?.id || '-';
  const vendorScore = getVendorScore(providerName);

  const updateBidStatus = async (status: 'accepted' | 'rejected') => {
    if (status === 'accepted') {
      await acceptBid(project.id, bid.id);
      navigate(`/owner/proyek-saya/${project.id}`);
      return;
    }

    await rejectBid(project.id, bid.id);
    navigate('/owner/penawaran-masuk');
  };

  const commercialItems = [
    { label: 'Nilai Penawaran', value: formatCurrency(getBidAmount(bid)), icon: Wallet },
    { label: 'Durasi Estimasi', value: bid.duration || '-', icon: Clock },
    { label: 'Tanggal Masuk', value: formatDate(bid.submitted_at || undefined), icon: Calendar },
    { label: 'Status', value: statusLabel[bid.status], icon: ShieldCheck },
  ];

  const projectItems = [
    ['Proyek', project.title],
    ['Kategori', project.category || '-'],
    ['Lokasi', project.location || project.city || '-'],
    ['Budget Owner', project.budget_range || formatCurrency(project.budget_max ?? project.budget_min ?? 0)],
    ['Tipe Pengadaan', project.procurement_type || '-'],
    ['Deadline Bid', formatDate(project.bid_deadline || undefined)],
  ];

  return (
    <div className="space-y-6">
      <Link to="/owner/penawaran-masuk" className="inline-flex items-center gap-2 text-sm font-extrabold text-blue-700">
        <ArrowLeft size={16} />
        Kembali ke Penawaran Masuk
      </Link>

      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="bg-gradient-to-br from-blue-700 to-slate-950 p-6 text-white">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <span className={`inline-flex rounded-full px-3 py-1 text-xs font-extrabold ${statusTone[bid.status]} bg-white/95`}>
                {statusLabel[bid.status]}
              </span>
              <h1 className="mt-4 text-3xl font-extrabold tracking-tight">{providerName}</h1>
              <p className="mt-2 text-sm font-semibold text-blue-100">{project.title}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {vendorScore.verified && (
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-2 text-sm font-extrabold text-white">
                  <ShieldCheck size={16} />
                  Verified Vendor
                </span>
              )}
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-2 text-sm font-extrabold text-white">
                <Star size={16} className="fill-amber-400 text-amber-400" />
                {vendorScore.rating} ({vendorScore.reviews})
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="min-w-0 space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-4">
            {commercialItems.map((item) => {
              const Icon = item.icon;

              return (
                <div key={item.label} className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <Icon size={21} className="text-blue-600" />
                  <p className="mt-3 text-xs font-bold text-slate-500">{item.label}</p>
                  <p className="mt-1 truncate text-sm font-extrabold text-slate-900">{item.value}</p>
                </div>
              );
            })}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-extrabold text-slate-900">Catatan Penawaran Provider</h2>
            <p className="mt-4 text-sm leading-7 text-slate-600">{getBidNote(bid)}</p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-extrabold text-slate-900">Metode Kerja Singkat</h2>
            <p className="mt-4 text-sm leading-7 text-slate-600">
              {bid.method_statement || 'Provider belum mengisi metode kerja singkat.'}
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-extrabold text-slate-900">Dokumen Pendukung Provider</h2>
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {bid.documents?.map((document) => (
                <div key={document.id} className="flex items-start justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-extrabold text-slate-900">{document.name}</p>
                    <p className="mt-1 text-xs font-semibold text-blue-700">
                      {bidDocumentCategoryLabels[document.category || 'other'] || 'Dokumen'}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">{formatFileSize(document.file_size ?? 0)} • {document.file_type || '-'}</p>
                  </div>
                  <button 
                    type="button" 
                    onClick={() => handleDownload(document)}
                    className="rounded-xl p-2 text-blue-600 transition hover:bg-blue-50" 
                    aria-label={`Unduh ${document.name}`}
                  >
                    <Download size={17} />
                  </button>
                </div>
              ))}
              {(!bid.documents || bid.documents.length === 0) && (
                <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm font-semibold text-slate-400 md:col-span-2">
                  Provider belum melampirkan dokumen portfolio, RAB, atau legalitas.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-extrabold text-slate-900">Data Proyek yang Ditawar</h2>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              {projectItems.map(([label, value]) => (
                <div key={label} className="min-w-0">
                  <p className="text-xs font-extrabold text-slate-500">{label}</p>
                  <p className="mt-1 break-words text-sm font-semibold leading-6 text-slate-800">{value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside className="w-full space-y-4 lg:sticky lg:top-24 lg:w-[340px]">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="font-extrabold text-slate-900">Aksi Review</h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              Terima provider jika harga, dokumen, dan metode kerjanya sudah sesuai.
            </p>
            <div className="mt-5 grid gap-3">
              <button
                onClick={() => updateBidStatus('accepted')}
                disabled={!isReviewableBid(bid) || isAcceptingBid}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-extrabold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                <CheckCircle2 size={17} />
                Terima Penawaran
              </button>
              <button 
                type="button"
                onClick={() => handleChatVendor(bid.provider?.user_id || bid.provider_id, projectId)}
                disabled={createChannelMutation.isPending}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm font-extrabold text-blue-700 transition hover:bg-blue-50 disabled:opacity-50"
              >
                <MessageSquare size={17} />
                {createChannelMutation.isPending ? 'Membuka...' : 'Chat Vendor'}
              </button>
              <button
                type="button"
                onClick={() => toggleVendorFavorite(providerId)}
                disabled={isAdding}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 px-4 py-3 text-sm font-extrabold text-blue-700 transition hover:bg-blue-50"
              >
                <Bookmark size={17} />
                {isVendorSaved(providerId) ? 'Hapus dari Tersimpan' : 'Simpan Vendor'}
              </button>
              <button
                onClick={() => updateBidStatus('rejected')}
                disabled={!isReviewableBid(bid) || isRejectingBid}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-100 px-4 py-3 text-sm font-extrabold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:border-slate-100 disabled:text-slate-300"
              >
                <XCircle size={17} />
                Tolak Penawaran
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5">
            <Briefcase size={28} className="text-blue-700" />
            <h2 className="mt-3 font-extrabold text-slate-900">Setelah Diterima</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Sistem akan membuka halaman Monitoring Owner. Provider Merchant melanjutkan baseline, delivery, validation, dan reporting di workspace operasional.
            </p>
          </div>
        </aside>
      </section>
    </div>
  );
};

export default PenawaranDetail;
